// THE WORDS FOR TIME AND COUNTS — one of each, for every page: "5m ago",
// "in 3h", "3 Oct 2026", "3 Oct", "3 Oct, 14:05", "2 nodes", "1,204". A time may come
// as a Date, an ISO string or epoch milliseconds; nothing (or nonsense) reads
// as nothing, never as "Invalid Date".

const DAY = 86400000;
// largest first: a span is said in the largest unit that fits it
const UNITS = [[31536000, 'y'], [2592000, 'mo'], [604800, 'w'], [86400, 'd'], [3600, 'h'], [60, 'm']];

const isEmpty = (at) => at == null || at === '';

/** Epoch milliseconds, or NaN for nothing or nonsense. */
function timeOf(at) {
    if (isEmpty(at)) return NaN;
    return at instanceof Date ? at.getTime() : new Date(at).getTime();
}

/** "5m", "3h", "2w": seconds in the largest unit that fits; under a minute, seconds. */
function span(s, round = Math.floor) {
    for (const [n, w] of UNITS) if (s >= n) return `${round(s / n)}${w}`;
    return `${Math.max(1, round(s))}s`;
}

/** "just now", "5m ago", "3h ago", "2d ago", "1w ago", "4mo ago", "1y ago".
 *  `never`: what nothing reads as (''); `dateAfter`: past that many days, the
 *  date itself; `bare`: no "ago" ("now", "5m"), for a row's compact corner. */
export function ago(at, { now = Date.now(), never = '', dateAfter = null, bare = false } = {}) {
    if (isEmpty(at)) return never;
    const t = timeOf(at);
    if (Number.isNaN(t)) return '';
    const s = Math.max(0, (now - t) / 1000);
    if (dateAfter != null && s >= dateAfter * 86400) return dateOf(t);
    if (s < 60) return bare ? 'now' : 'just now';
    return bare ? span(s) : `${span(s)} ago`;
}

/** "in 19h"; "now" within the minute, and a time already past says so
 *  ("due 3h ago") rather than claiming it is about to happen. `days`: by the
 *  day instead — "today", "tomorrow", "in 12 days". */
export function until(at, { now = Date.now(), days = false } = {}) {
    const t = timeOf(at);
    if (Number.isNaN(t)) return '';
    if (days) {
        const d = Math.floor((t - now) / DAY);
        return d <= 0 ? 'today' : d === 1 ? 'tomorrow' : `in ${d} days`;
    }
    const s = (now - t) / 1000;
    if (s <= 0) return `in ${span(-s, Math.round)}`;
    return s < 60 ? 'now' : `due ${span(s, Math.round)} ago`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "3 Oct 2026": day, short month, year, in that order whatever the
 *  browser's locale (built from parts, not toLocaleDateString). */
export function dateOf(at) {
    const t = timeOf(at);
    if (Number.isNaN(t)) return '';
    const d = new Date(t);
    return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** "3 Oct": a day this year or near it, where the year goes without saying. */
export function dayOf(at) {
    const t = timeOf(at);
    if (Number.isNaN(t)) return '';
    const d = new Date(t);
    return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/** "3 Oct, 14:05"; with `year`, "3 Oct 2026, 14:05". */
export function whenOf(at, { year = false } = {}) {
    const t = timeOf(at);
    if (Number.isNaN(t)) return '';
    return new Date(t).toLocaleString(undefined, { day: 'numeric', month: 'short', ...(year ? { year: 'numeric' } : {}), hour: 'numeric', minute: '2-digit' });
}

/** A count as people read it: "1,204". */
export const fmt = (n) => Number(n || 0).toLocaleString();

/** "1 node", "2 nodes", "1,204 people" (plural(n, 'person', 'people')). */
export const plural = (n, word, words = `${word}s`) => `${fmt(n)} ${n === 1 ? word : words}`;
